import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BlogCard = ({ post }) => {
  const location = useLocation();

  const handleCardClick = () => {
    sessionStorage.setItem(`scroll-${location.pathname}`, window.scrollY.toString());
  };
  const getCategoryColor = (category) => {
    const colors = {
      'design-process': 'bg-accent/20 text-accent-light',
      'client-collaboration': 'bg-success/20 text-success-light',
      'industry-insights': 'bg-warning/20 text-warning-light',
      'tools-techniques': 'bg-error/20 text-error-light',
      'career-growth': 'bg-primary-900/40 text-primary-300'
    };
    return colors[category] || 'bg-white/10 text-text-secondary';
  };

  return (
    <article className="bg-white/5 backdrop-blur-xl rounded-xl overflow-hidden border border-white/10 shadow-2xl hover:shadow-accent/5 transition-smooth group">
      {/* Image */}
      <div className="h-48 overflow-hidden">
        <Image
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700"
        />
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Category & Meta */}
        <div className="flex items-center justify-between mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.categoryId)}`}>
            {post.category}
          </span>
          {post.featured === 1 ? (
            <span className="bg-warning/10 text-warning px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <Icon name="Star" size={10} className="fill-current" />
              Featured
            </span>
          ) : null}
          {post.isNew ? (
            <span className="bg-error/10 text-error px-2 py-1 rounded-full text-xs font-medium">
              New
            </span>
          ) : null}
        </div>

        {/* Title */}
        <h3 className="font-heading text-lg font-semibold text-text-primary mb-3 leading-tight group-hover:text-accent transition-smooth">
          <Link 
            to={`/blog/${post.slug || post.id}`} 
            onClick={handleCardClick} 
            className="hover:underline"
            aria-label={`Read the article: ${post.title}`}
            title={post.title}
          >
            {post.title}
          </Link>
        </h3>

        {/* Excerpt */}
        <p className="font-body text-text-secondary text-sm mb-4 leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>

        {/* Key Takeaways */}
        {post.takeaways && post.takeaways.length > 0 && (
          <div className="mb-4">
            <h4 className="font-body text-xs font-semibold text-text-primary mb-2 uppercase tracking-wide">
              Key Takeaways:
            </h4>
            <ul className="space-y-1">
              {post.takeaways.slice(0, 2).map((takeaway, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-text-secondary">
                  <Icon name="Check" size={12} className="text-success mt-0.5 flex-shrink-0" />
                  <span>{takeaway}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-text-muted border-t border-border-muted pt-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Icon name="Calendar" size={12} />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Clock" size={12} />
              <span>{post.readTime}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Eye" size={12} />
            <span>{post.views || 0}</span>
          </div>
        </div>

      {/* Read More Link */}
        <Link
          to={`/blog/${post.slug || post.id}`}
          onClick={handleCardClick}
          className="inline-flex items-center gap-1 text-accent hover:text-accent-600 font-medium text-sm mt-4 group-hover:gap-2 transition-smooth"
          aria-label={`Continue reading: ${post.title}`}
          title={`Read ${post.title}`}
        >
          Read More
          <Icon name="ArrowRight" size={14} />
        </Link>
      </div>
    </article>
  );
};

export default BlogCard;